import { webContainerService } from './webContainerService';
import { claudeCodeService } from './claudeCodeService';
import { websocketService } from './websocketService';

class WebContainerIntegration {
  constructor() {
    this.container = null;
    this.projectId = null;
    this.isConnected = false;
    this.fileWatchers = new Map();
  }

  async initialize(projectId) {
    this.projectId = projectId;
    this.container = await webContainerService.boot();
    this.isConnected = true;
    
    // Set up WebSocket listeners for backend file operations
    this.setupWebSocketListeners();
    
    return this.container;
  }

  setupWebSocketListeners() {
    // Listen for file operations from backend
    websocketService.on('fileOperationLive', this.handleBackendFileOperation.bind(this));
    websocketService.on('directoryOperationLive', this.handleBackendDirectoryOperation.bind(this));
    websocketService.on('codeContentLive', this.handleBackendCodeGeneration.bind(this));
    websocketService.on('workspaceUpdate', this.handleWorkspaceUpdate.bind(this));
  }

  async handleBackendFileOperation(data) {
    if (!this.container || data.project_id !== this.projectId) return;

    const { file_path, directory_path, filename, operation, content, context } = data;
    
    console.log('📂 Backend file operation:', {
      file_path,
      directory_path,
      filename,
      operation,
      content_length: content?.length || 0
    });
    
    try {
      // Use the full content if provided, otherwise fall back to context
      const fileContent = content || context;
      
      switch (operation) {
        case 'creating':
          await this.createFileFromBackend(file_path, fileContent, directory_path);
          break;
        case 'editing':
          await this.updateFileFromBackend(file_path, fileContent);
          break;
        case 'updating':
          await this.updateFileFromBackend(file_path, fileContent);
          break;
      }
    } catch (error) {
      console.error('Failed to sync backend file operation:', error);
    }
  }

  async handleBackendDirectoryOperation(data) {
    if (!this.container || data.project_id !== this.projectId) return;

    const { directory_path, operation } = data;
    
    console.log('📁 Backend directory operation:', {
      directory_path,
      operation
    });
    
    try {
      if (operation === 'ensuring' || operation === 'creating') {
        await this.ensureDirectory(directory_path);
      }
    } catch (error) {
      console.error('Failed to sync backend directory operation:', error);
    }
  }

  async handleBackendCodeGeneration(data) {
    if (!this.container || data.project_id !== this.projectId) return;

    const { language, code, code_type } = data;
    
    // Determine file extension and path based on language
    const fileExtensions = {
      'javascript': '.js',
      'typescript': '.ts',
      'python': '.py',
      'html': '.html',
      'css': '.css',
      'json': '.json'
    };
    
    const extension = fileExtensions[language] || '.txt';
    const fileName = `generated_${Date.now()}${extension}`;
    const filePath = `src/${fileName}`;
    
    await this.createFile(filePath, code);
  }

  async handleWorkspaceUpdate(data) {
    if (!this.container || data.project_id !== this.projectId) return;
    
    // Refresh the entire file system from backend
    await this.syncWithBackend();
  }

  async createFileFromBackend(filePath, content, directoryPath = null) {
    try {
      // Extract content from context if it's a description
      let fileContent = content;
      
      // If content is a description, fetch actual content from backend
      if (content && content.length < 100 && !content.includes('\n')) {
        const response = await claudeCodeService.readFile(this.projectId, filePath);
        if (response.success) {
          fileContent = response.content;
        } else {
          // Generate placeholder content based on file type
          fileContent = this.generatePlaceholderContent(filePath);
        }
      }
      
      // Ensure directory structure exists first
      if (directoryPath) {
        await this.ensureDirectory(directoryPath);
      }
      
      await this.createFile(filePath, fileContent);
    } catch (error) {
      console.error('Failed to create file from backend:', error);
    }
  }

  async ensureDirectory(directoryPath) {
    if (!this.container || !directoryPath || directoryPath === '.') return;

    try {
      // Split the directory path and create each level
      const pathParts = directoryPath.split('/').filter(part => part);
      let currentPath = '';
      
      for (const part of pathParts) {
        currentPath += (currentPath ? '/' : '') + part;
        try {
          await this.container.fs.readdir(currentPath);
          console.log(`📁 Directory exists: ${currentPath}`);
        } catch {
          // Directory doesn't exist, create it
          await this.container.fs.mkdir(currentPath);
          console.log(`📁 Created directory: ${currentPath}`);
        }
      }
    } catch (error) {
      console.error(`Failed to ensure directory ${directoryPath}:`, error);
    }
  }

  async updateFileFromBackend(filePath, content) {
    try {
      // Fetch latest content from backend
      const response = await claudeCodeService.readFile(this.projectId, filePath);
      if (response.success) {
        await this.updateFile(filePath, response.content);
      }
    } catch (error) {
      console.error('Failed to update file from backend:', error);
    }
  }

  async createFile(filePath, content) {
    if (!this.container) return;

    try {
      // Ensure parent directories exist
      const pathParts = filePath.split('/');
      let currentPath = '';
      
      for (let i = 0; i < pathParts.length - 1; i++) {
        currentPath += (currentPath ? '/' : '') + pathParts[i];
        try {
          await this.container.fs.readdir(currentPath);
        } catch {
          // Directory doesn't exist, create it
          await this.container.fs.mkdir(currentPath);
        }
      }
      
      // Write the file
      await this.container.fs.writeFile(filePath, content);
      console.log(`✅ Created file: ${filePath}`);
      
      // Notify listeners
      this.notifyFileCreated(filePath, content);
      
    } catch (error) {
      console.error(`Failed to create file ${filePath}:`, error);
    }
  }

  async updateFile(filePath, content) {
    if (!this.container) return;

    try {
      await this.container.fs.writeFile(filePath, content);
      console.log(`📝 Updated file: ${filePath}`);
      
      // Notify listeners
      this.notifyFileUpdated(filePath, content);
      
    } catch (error) {
      console.error(`Failed to update file ${filePath}:`, error);
    }
  }

  async readFile(filePath) {
    if (!this.container) return null;

    try {
      const content = await this.container.fs.readFile(filePath, 'utf-8');
      return content;
    } catch (error) {
      console.error(`Failed to read file ${filePath}:`, error);
      return null;
    }
  }

  async createProject(projectTemplate = 'express') {
    if (!this.container) return;

    const templates = {
      express: this.createExpressProject.bind(this),
      react: this.createReactProject.bind(this),
      vue: this.createVueProject.bind(this),
      nextjs: this.createNextJSProject.bind(this),
      python: this.createPythonProject.bind(this)
    };

    const template = templates[projectTemplate] || templates.express;
    await template();
  }

  async createExpressProject() {
    const files = {
      'package.json': JSON.stringify({
        "name": `project-${this.projectId}`,
        "version": "1.0.0",
        "description": "Express.js application generated by AI",
        "main": "server.js",
        "scripts": {
          "start": "node server.js",
          "dev": "nodemon server.js",
          "test": "echo \"Error: no test specified\" && exit 1"
        },
        "dependencies": {
          "express": "^4.18.2",
          "cors": "^2.8.5",
          "dotenv": "^16.0.3"
        },
        "devDependencies": {
          "nodemon": "^2.0.22"
        }
      }, null, 2),

      'server.js': `const express = require('express');
const cors = require('cors');
require('dotenv').config();

const app = express();
const PORT = process.env.PORT || 3000;

// Middleware
app.use(cors());
app.use(express.json());
app.use(express.static('public'));

// Routes
app.get('/', (req, res) => {
  res.json({ 
    message: 'Welcome to your AI-generated Express.js application!',
    status: 'running',
    timestamp: new Date().toISOString()
  });
});

app.get('/api/health', (req, res) => {
  res.json({ status: 'healthy', uptime: process.uptime() });
});

// Start server
app.listen(PORT, () => {
  console.log(\`🚀 Server running on http://localhost:\${PORT}\`);
});`,

      '.env': `PORT=3000
NODE_ENV=development`,

      'README.md': `# AI-Generated Express.js Application

This Express.js application was generated using AI-powered development tools.

## Getting Started

1. Install dependencies:
   \`\`\`bash
   npm install
   \`\`\`

2. Start the development server:
   \`\`\`bash
   npm run dev
   \`\`\`

3. Open http://localhost:3000 in your browser

## API Endpoints

- \`GET /\` - Welcome message
- \`GET /api/health\` - Health check

## Development

- \`npm start\` - Start production server
- \`npm run dev\` - Start development server with auto-reload
- \`npm test\` - Run tests

## Features

- Express.js web framework
- CORS enabled
- Environment variables support
- Static file serving
- JSON request parsing

Happy coding! 🎉`,

      'public/index.html': `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>AI-Generated App</title>
    <style>
        body { font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 20px; }
        .header { color: #333; border-bottom: 2px solid #007acc; padding-bottom: 10px; }
        .status { background: #e7f5e7; padding: 15px; border-radius: 8px; margin: 20px 0; }
        .api-section { background: #f5f5f5; padding: 15px; border-radius: 8px; margin: 20px 0; }
        pre { background: #2d2d2d; color: #fff; padding: 10px; border-radius: 4px; overflow-x: auto; }
    </style>
</head>
<body>
    <h1 class="header">🚀 AI-Generated Express.js Application</h1>
    
    <div class="status">
        <h2>✅ Application Status</h2>
        <p><strong>Server:</strong> Running</p>
        <p><strong>Framework:</strong> Express.js</p>
        <p><strong>Environment:</strong> Development</p>
    </div>

    <div class="api-section">
        <h2>🔌 API Endpoints</h2>
        <p><strong>GET /</strong> - Welcome message</p>
        <p><strong>GET /api/health</strong> - Health check</p>
        
        <h3>Try the API:</h3>
        <pre><code>curl http://localhost:3000/
curl http://localhost:3000/api/health</code></pre>
    </div>

    <script>
        // Test API connectivity
        fetch('/api/health')
            .then(response => response.json())
            .then(data => {
                console.log('API Health Check:', data);
                document.querySelector('.status').innerHTML += 
                    \`<p><strong>API Status:</strong> \${data.status}</p>\`;
            })
            .catch(error => {
                console.error('API Error:', error);
            });
    </script>
</body>
</html>`
    };

    // Create all files
    for (const [filePath, content] of Object.entries(files)) {
      await this.createFile(filePath, content);
    }
  }

  async createReactProject() {
    const files = {
      'package.json': JSON.stringify({
        "name": `react-project-${this.projectId}`,
        "version": "0.1.0",
        "private": true,
        "dependencies": {
          "react": "^18.2.0",
          "react-dom": "^18.2.0",
          "react-scripts": "5.0.1"
        },
        "scripts": {
          "start": "react-scripts start",
          "build": "react-scripts build",
          "test": "react-scripts test",
          "eject": "react-scripts eject"
        },
        "browserslist": {
          "production": [">0.2%", "not dead", "not op_mini all"],
          "development": ["last 1 chrome version", "last 1 firefox version", "last 1 safari version"]
        }
      }, null, 2),

      'public/index.html': `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>AI-Generated React App</title>
</head>
<body>
    <noscript>You need to enable JavaScript to run this app.</noscript>
    <div id="root"></div>
</body>
</html>`,

      'src/index.js': `import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<App />);`,

      'src/App.js': `import React from 'react';
import './App.css';

function App() {
  return (
    <div className="App">
      <header className="App-header">
        <h1>🚀 AI-Generated React Application</h1>
        <p>Welcome to your React app created by AI!</p>
        <p>Edit <code>src/App.js</code> and save to reload.</p>
      </header>
    </div>
  );
}

export default App;`,

      'src/App.css': `.App {
  text-align: center;
}

.App-header {
  background-color: #282c34;
  padding: 20px;
  color: white;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: calc(10px + 2vmin);
}

code {
  background-color: #f1f1f1;
  padding: 2px 4px;
  border-radius: 3px;
  font-family: source-code-pro, Menlo, Monaco, Consolas, monospace;
}`
    };

    for (const [filePath, content] of Object.entries(files)) {
      await this.createFile(filePath, content);
    }
  }

  async installDependencies() {
    if (!this.container) return;

    try {
      console.log('📦 Installing dependencies...');
      const installProcess = await this.container.spawn('npm', ['install']);
      
      // Stream output
      if (installProcess.output) {
        const reader = installProcess.output.getReader();
        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            
            const text = new TextDecoder().decode(value);
            console.log('📦', text);
            this.notifyInstallProgress(text);
          }
        } catch (error) {
          console.error('Error reading install output:', error);
        }
      }
      
      await installProcess.exit;
      console.log('✅ Dependencies installed successfully');
      this.notifyInstallComplete();
      
    } catch (error) {
      console.error('Failed to install dependencies:', error);
      this.notifyInstallError(error.message);
    }
  }

  async startDevServer() {
    if (!this.container) return null;

    try {
      console.log('🚀 Starting development server...');
      const serverProcess = await this.container.spawn('npm', ['start']);
      
      // Listen for server ready message
      if (serverProcess.output) {
        const reader = serverProcess.output.getReader();
        const decoder = new TextDecoder();
        
        const checkOutput = async () => {
          try {
            while (true) {
              const { done, value } = await reader.read();
              if (done) break;
              
              const text = decoder.decode(value);
              console.log('🚀', text);
              
              // Look for server start message
              if (text.includes('localhost') || text.includes('127.0.0.1')) {
                const portMatch = text.match(/:(\d+)/);
                if (portMatch) {
                  const port = portMatch[1];
                  const url = `http://localhost:${port}`;
                  this.notifyServerStarted(url);
                  return url;
                }
              }
            }
          } catch (error) {
            console.error('Error reading server output:', error);
          }
        };
        
        checkOutput();
      }
      
      return serverProcess;
      
    } catch (error) {
      console.error('Failed to start dev server:', error);
      this.notifyServerError(error.message);
      return null;
    }
  }

  generatePlaceholderContent(filePath) {
    const ext = filePath.split('.').pop()?.toLowerCase();
    
    const placeholders = {
      'js': `// ${filePath}
console.log('Generated by AI - ${new Date().toISOString()}');`,
      'py': `# ${filePath}
print("Generated by AI - ${new Date().toISOString()}")`,
      'html': `<!DOCTYPE html>
<html>
<head><title>${filePath}</title></head>
<body><h1>Generated by AI</h1></body>
</html>`,
      'css': `/* ${filePath} */
body { font-family: Arial, sans-serif; }`,
      'json': `{
  "name": "${filePath}",
  "generated": "${new Date().toISOString()}"
}`
    };
    
    return placeholders[ext] || `// ${filePath}\n// Generated by AI - ${new Date().toISOString()}`;
  }

  async syncWithBackend() {
    try {
      console.log('🔄 Syncing with backend workspace...');
      const workspaceStatus = await claudeCodeService.getWorkspaceStatus(this.projectId);
      
      if (workspaceStatus.success && workspaceStatus.data) {
        const { files, directories, directory_tree } = workspaceStatus.data;
        
        console.log('📊 Workspace status:', {
          files: files?.length || 0,
          directories: directories?.length || 0,
          tree: directory_tree
        });
        
        // First, ensure all directories exist
        if (directories && directories.length > 0) {
          for (const dirPath of directories) {
            await this.ensureDirectory(dirPath);
          }
        }
        
        // Then sync all files with proper directory structure
        if (files && files.length > 0) {
          for (const file of files) {
            try {
              // Ensure the directory exists before creating the file
              if (file.directory) {
                await this.ensureDirectory(file.directory);
              }
              
              // Read the file content from backend
              const content = await claudeCodeService.readFile(this.projectId, file.path);
              if (content.success) {
                await this.updateFile(file.path, content.content);
                console.log(`✅ Synced file: ${file.path}`);
              } else {
                console.log(`⚠️ Failed to read file content: ${file.path}`);
              }
            } catch (error) {
              console.error(`❌ Failed to sync file ${file.path}:`, error);
            }
          }
        }
        
        // Notify that sync is complete
        this.notifySyncComplete(workspaceStatus.data);
        
      } else {
        console.log('⚠️ No workspace data to sync');
      }
    } catch (error) {
      console.error('Failed to sync with backend:', error);
    }
  }

  // Notification methods
  notifyFileCreated(filePath, content) {
    window.dispatchEvent(new CustomEvent('webcontainer:fileCreated', {
      detail: { filePath, content, size: content.length }
    }));
  }

  notifyFileUpdated(filePath, content) {
    window.dispatchEvent(new CustomEvent('webcontainer:fileUpdated', {
      detail: { filePath, content, size: content.length }
    }));
  }

  notifyInstallProgress(output) {
    window.dispatchEvent(new CustomEvent('webcontainer:installProgress', {
      detail: { output }
    }));
  }

  notifyInstallComplete() {
    window.dispatchEvent(new CustomEvent('webcontainer:installComplete'));
  }

  notifyInstallError(error) {
    window.dispatchEvent(new CustomEvent('webcontainer:installError', {
      detail: { error }
    }));
  }

  notifyServerStarted(url) {
    window.dispatchEvent(new CustomEvent('webcontainer:serverStarted', {
      detail: { url }
    }));
  }

  notifyServerError(error) {
    window.dispatchEvent(new CustomEvent('webcontainer:serverError', {
      detail: { error }
    }));
  }

  notifySyncComplete(workspaceData) {
    window.dispatchEvent(new CustomEvent('webcontainer:syncComplete', {
      detail: { workspaceData }
    }));
  }

  getContainer() {
    return this.container;
  }

  isReady() {
    return this.isConnected && this.container !== null;
  }
}

// Export singleton instance
export const webContainerIntegration = new WebContainerIntegration();
export default webContainerIntegration;